import React, { useState } from "react";
import FieldElements from "../../UniversalComponents/FieldElements";
import LabelComponent from "../../UniversalComponents/LabelComponent";
import { ReactComponent as CloseEyeIcon } from "../assets/closeEyeIcon.svg";
import { ReactComponent as OpenEyeIcon } from "../assets/view_eyeIcon.svg";
import { CHECK_EMAIL_DUPLICATE, GOOGLE_CLIENT, REDIRECT_URL } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";

const GOOGLE_CLIENT_ID = GOOGLE_CLIENT;
const GOOGLE_REDIRECT_URL = REDIRECT_URL; // must match Google Console setting
// const GOOGLE_REDIRECT_URL = "http://localhost:3000";

function base64url(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256(buffer) {
  return await crypto.subtle.digest("SHA-256", buffer);
}

function randomString(len = 64) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function SignUpForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });

  const [showPassword, setShowPassword] = React.useState({
    password: false,
    confirmPassword: false,
  });

  const [errorMessage, setErrorMessage] = useState({});
  const isStrongPassword = (password) => {
    return {
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
      hasMinLength: password.length >= 8,
    };
  };
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(`${CHECK_EMAIL_DUPLICATE}?email=${email}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data?.exists) {
        setErrorMessage((prev) => ({
          ...prev,
          email: "User Already Exists",
        }));
      } else {
        setErrorMessage((prev) => {
          const { email, ...rest } = prev || {};
          return rest;
        });
      }
    } catch (error) {
      console.error("Error checking email:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};

    setErrorMessage((prev) => {
      const { [name]: removed, ...rest } = prev || {};
      return rest;
    });

    if (name == "email") {
      checkEmailExists(value);
    }

    if (name === "confirmPassword") {
      if (formData.password !== value) {
        setErrorMessage((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setErrorMessage((prev) => {
          const { confirmPassword, ...rest } = prev || {};
          return rest;
        });
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSignUp = () => {
    const { fullName, email, password, confirmPassword, agreeToTerms } = formData;

    //will refactor later on , by using a single component, utility function

    //for email already exists
    if (errorMessage?.email) {
      return;
    }

    // Full Name validation
    if (!fullName.trim()) {
      setErrorMessage((prev) => ({
        ...prev,
        fullName: "Full name is required.",
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { fullName, ...rest } = prev || {};
        return rest;
      });
    }

    // Email validation
    if (!email.trim()) {
      setErrorMessage((prev) => ({
        ...prev,
        email: "Email is required.",
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { email, ...rest } = prev || {};
        return rest;
      });
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage((prev) => ({
        ...prev,
        email: "Please enter a valid email address.",
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { email, ...rest } = prev || {};
        return rest;
      });
    }

    // Password and Confirm Password empty check
    if (!password.trim()) {
      setErrorMessage((prev) => ({
        ...prev,
        password: "Password is required",
      }));
      return;
    }

    const pwdCheck = isStrongPassword(password);
    const pwdErrors = Object.entries(pwdCheck)
      .filter(([_, passed]) => !passed)
      .map(([key]) => {
        switch (key) {
          case "hasUppercase":
            return "uppercase letter";
          case "hasLowercase":
            return "lowercase letter";
          case "hasNumber":
            return "number";
          case "hasSpecialChar":
            return "special character";
          case "hasMinLength":
            return "8 characters";
          default:
            return "";
        }
      });

    if (pwdErrors.length > 0) {
      setErrorMessage((prev) => ({
        ...prev,
        password: `No ${pwdErrors.join(", ")}`,
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { password, ...rest } = prev || {};
        return rest;
      });
    }

    if (!confirmPassword.trim()) {
      setErrorMessage((prev) => ({
        ...prev,
        confirmPassword: "Password is required",
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { confirmPassword, ...rest } = prev || {};
        return rest;
      });
    }

    // Password match check
    if (password !== confirmPassword) {
      setErrorMessage((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    } else {
      setErrorMessage((prev) => {
        const { password, ...rest } = prev || {};
        return rest;
      });
    }

    setErrorMessage(null);

    navigate("/onboarding/companySetup", { state: { signupData: formData } });
  };

  const handleGoogleSignUp = async () => {
    const codeVerifier = randomString(64);
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);

    const encoder = new TextEncoder().encode(codeVerifier);
    const hashed = await sha256(encoder);
    const codeChallenge = base64url(hashed);

    const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    url.searchParams.set("client_id", GOOGLE_CLIENT_ID);
    url.searchParams.set("redirect_uri", GOOGLE_REDIRECT_URL); // match what's registered
    url.searchParams.set("response_type", "code");
    url.searchParams.set("scope", "openid email profile");
    url.searchParams.set("code_challenge", codeChallenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("prompt", "select_account");

    window.location.assign(url.toString());
  };

  console.log("errorMessage", errorMessage);

  return (
    <section className="w-1/2 flex flex-col justify-between h-full overflow-y-auto items-center bg-white  font-Inter pt-8">
      <div className="flex flex-col gap-4 overflow-y-auto items-center w-full max-w-lg bg-white  px-4">
        {/* Card */}
        <div className="flex flex-col gap-8 w-full rounded-md">
          <header className="flex flex-col gap-1 items-center w-full">
            <h2 className="text-3xl font-bold tracking-wide text-center text-[#1D4469] sm:text-2xl">
              Create an Account
            </h2>
            <p className="text-base text-center text-cyan-900 sm:text-sm">
              Join thousands of financial professionals using our platform
            </p>
          </header>

          {/* Form */}
          <form
            className="flex flex-col  w-full"
            onSubmit={(e) => {
              e.preventDefault();
              handleSignUp();
            }}
            autoComplete="off"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <LabelComponent labelText="Full Name" size="medium" className="text-[#313131cc] font-medium" />
                  {errorMessage?.fullName && <p className="text-F16136 text-xs">{errorMessage.fullName}</p>}
                </div>

                <FieldElements
                  label="Full Name"
                  name="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  mandatory
                  height="3.6rem"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <LabelComponent labelText="Email Address" size="medium" className="text-[#313131cc] font-medium" />
                  {errorMessage?.email && <p className="text-F16136 text-xs">{errorMessage.email}</p>}
                </div>

                <FieldElements
                  label="Email Address"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  mandatory
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <LabelComponent labelText="Password" size="medium" className="text-[#313131cc] font-medium" />
                  {errorMessage?.password && <p className="text-F16136 text-xs">{errorMessage.password}</p>}
                </div>

                <FieldElements
                  label="Password"
                  name="password"
                  type="text"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  mandatory
                  inputType={showPassword.password ? "text" : "password"}
                  InputEndIcon={
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("password")}
                      className="flex items-center text-gray-500"
                      aria-label={showPassword.password ? "Hide password" : "Show password"}
                    >
                      {showPassword.password ? <OpenEyeIcon /> : <CloseEyeIcon />}
                    </button>
                  }
                />

                {/* ✅ Password Strength Rules */}
                {formData.password && (
                  <ul className=" text-xs space-y-1 px-1 grid grid-cols-2">
                    {[
                      {
                        label: "At least one uppercase letter",
                        test: /[A-Z]/.test(formData.password),
                      },
                      {
                        label: "At least one lowercase letter",
                        test: /[a-z]/.test(formData.password),
                      },
                      {
                        label: "At least one number",
                        test: /[0-9]/.test(formData.password),
                      },
                      {
                        label: "At least one special character",
                        test: /[^A-Za-z0-9]/.test(formData.password),
                      },
                      {
                        label: "Minimum 8 characters",
                        test: formData.password.length >= 8,
                      },
                    ].map(({ label, test }) => (
                      <li
                        key={label}
                        className={`flex items-center gap-2 ${test ? "text-green-600" : "text-[#F16136]"}`}
                      >
                        <span className="text-sm">{test ? "✓" : "✕"}</span>
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <LabelComponent labelText="Confirm Password" size="medium" className="text-[#313131cc] font-medium" />
                  {errorMessage?.confirmPassword && (
                    <p className="text-F16136 text-xs">{errorMessage.confirmPassword}</p>
                  )}
                </div>

                <FieldElements
                  label="Confirm Password"
                  name="confirmPassword"
                  type="text"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Re-enter your password"
                  mandatory
                  inputType={showPassword.confirmPassword ? "text" : "password"}
                  InputEndIcon={
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("confirmPassword")}
                      className="flex items-center text-gray-500"
                      aria-label={showPassword.confirmPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword.confirmPassword ? <OpenEyeIcon /> : <CloseEyeIcon />}
                    </button>
                  }
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              {/* <LabelComponent labelText="I agree to the Terms and Privacy Policy" size="medium" className="text-[#313131cc] font-medium" />
              <FieldElements
                label="I agree to the Terms and Privacy Policy"
                name="agreeToTerms"
                type="checkbox"
                value={formData.agreeToTerms}
                onChange={handleInputChange}
                checked={formData.agreeToTerms}
                mandatory
                
              /> */}

              <label className="flex items-center gap-2 text-[#666] font-normal text-sm pt-2">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="text-[#1D4469] border border-[#1D4469] rounded-sm cursor-pointer outline-none focus:outline-none focus:ring-0 focus:ring-transparent"
                />
                I agree with
                <a href="#" className=" text-[#1D4469] font-medium underline text-sm">
                  Terms of Use
                </a>
                &
                <a href="#" className=" text-[#1D4469] font-medium underline text-sm">
                  Privacy Policy
                </a>
              </label>
            </div>

            <div className="flex flex-col gap-5 py-4">
              <button
                disabled={!formData.agreeToTerms || Object.keys(errorMessage).length > 0}
                type="submit"
                className={`flex justify-center items-center w-full rounded-md border py-2 transition-colors
                ${
                  !formData.agreeToTerms || Object.keys(errorMessage).length > 0
                    ? "bg-cyan-900 border-cyan-900 opacity-50 cursor-not-allowed"
                    : "bg-cyan-900 border-cyan-900 hover:bg-cyan-800"
                }`}
              >
                <span className="text-lg font-bold text-white sm:text-base">Sign Up</span>
              </button>
              <button
                type="button"
                onClick={handleGoogleSignUp}
                className="flex justify-center items-center gap-2 w-full rounded-md border border-zinc-200 py-2 hover:bg-gray-50 transition-colors"
              >
                {/* Google SVG */}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_365_3346)">
                    <path
                      d="M23.094 9.91355L13.3046 9.91309C12.8724 9.91309 12.522 10.2634 12.522 10.6957V13.823C12.522 14.2552 12.8724 14.6056 13.3046 14.6056H18.8174C18.2137 16.1722 17.087 17.4842 15.6496 18.3178L18.0002 22.387C21.7709 20.2062 24.0002 16.3799 24.0002 12.0965C24.0002 11.4866 23.9553 11.0506 23.8653 10.5597C23.797 10.1867 23.4732 9.91355 23.094 9.91355Z"
                      fill="#167EE6"
                    />
                    <path
                      d="M11.9998 19.3047C9.30193 19.3047 6.94675 17.8306 5.68182 15.6494L1.61279 17.9948C3.6835 21.5836 7.56259 24.0003 11.9998 24.0003C14.1765 24.0003 16.2304 23.4143 17.9998 22.3929V22.3873L15.6491 18.3181C14.5739 18.9417 13.3297 19.3047 11.9998 19.3047Z"
                      fill="#12B347"
                    />
                    <path
                      d="M18 22.3922V22.3866L15.6494 18.3174C14.5741 18.941 13.33 19.304 12 19.304V23.9996C14.1767 23.9996 16.2308 23.4135 18 22.3922Z"
                      fill="#0F993E"
                    />
                    <path
                      d="M4.69566 12.0003C4.69566 10.6705 5.05856 9.42637 5.68205 8.3512L1.61302 6.00586C0.586031 7.76962 0 9.81797 0 12.0003C0 14.1826 0.586031 16.2309 1.61302 17.9947L5.68205 15.6494C5.05856 14.5742 4.69566 13.3301 4.69566 12.0003Z"
                      fill="#FFD500"
                    />
                    <path
                      d="M11.9998 4.69566C13.759 4.69566 15.375 5.32078 16.6372 6.36061C16.9486 6.61711 17.4012 6.59859 17.6864 6.31336L19.9022 4.09758C20.2258 3.77395 20.2028 3.24422 19.8571 2.94431C17.7423 1.10967 14.9907 0 11.9998 0C7.56259 0 3.6835 2.41673 1.61279 6.00558L5.68182 8.35092C6.94675 6.16969 9.30193 4.69566 11.9998 4.69566Z"
                      fill="#FF4B26"
                    />
                    <path
                      d="M16.6374 6.36061C16.9488 6.61711 17.4015 6.59859 17.6866 6.31336L19.9024 4.09758C20.226 3.77395 20.2029 3.24422 19.8573 2.94431C17.7425 1.10963 14.991 0 12 0V4.69566C13.7592 4.69566 15.3752 5.32078 16.6374 6.36061Z"
                      fill="#D93F21"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_365_3346">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-lg font-medium text-[#666] sm:text-base">Continue with Google</span>
              </button>
            </div>
          </form>
        </div>

        {/* Divider */}
        <div className="border-t border-dashed border-[#DFE2E5] w-full max-w-xs mx-auto" />

        {/* Already have an account */}
        <div className="flex flex-col items-center w-full mt-4">
          <p className="text-sm font-normal text-center text-[#1a1a1a99] ">
            Already have an account?{" "}
            <span
              onClick={() => {
                navigate("/");
              }}
              className="text-[#1D4469] cursor-pointer hover:underline font-medium"
            >
              Login now.
            </span>
          </p>
        </div>
      </div>

      {/* Trial Bar */}
      <div className="flex w-full  mx-auto mt-8 px-4 py-2 bg-[#00b38614] rounded-md justify-center items-center">
        <p className="text-sm text-center text-[#1a1a1a99] font-normal  sm:text-xs">
          By signing up, you'll get a <span className="text-[#00B386] font-medium">30-day free trial</span> with access
          to all premium features
        </p>
      </div>
    </section>
  );
}
